/**
 * API Error Handling Utilities
 *
 * Provides standardized error handling for API requests, including detection of
 * non-retryable errors (4xx status codes) to prevent infinite retry loops.
 */

/**
 * HTTP status codes that indicate non-retryable errors.
 * These errors represent client errors that won't be resolved by retrying.
 */
export const NON_RETRYABLE_STATUS_CODES = [400, 401, 403, 404] as const;

/**
 * Keywords in error messages that indicate non-retryable errors.
 * Used when status codes aren't available in the error object.
 */
export const NON_RETRYABLE_KEYWORDS = [
  "not found",
  "404",
  "forbidden",
  "403",
  "unauthorized",
  "401",
  "bad request",
  "400",
  "resource not found",
  "page not found",
  "endpoint not found"
] as const;

/**
 * Error payload returned by createApiErrorPayload.
 */
export interface ApiErrorPayload {
  /**
   * Human-readable error message extracted from the error.
   */
  error: string;

  /**
   * Whether the error is non-retryable (e.g., 404, 403, 401, 400).
   * Non-retryable errors should not trigger automatic retries.
   */
  isNonRetryable?: boolean;
}

/**
 * Turns a validation `detail` MAP (field -> message) into one readable string.
 *
 * Some API validation errors (HTTP 400) return a generic `message` alongside a
 * `detail` OBJECT that names the offending field(s), e.g.
 * `{ "variables.env.MYVAR": "String does not match expected pattern" }`. We fold
 * that per-field detail into a single line so the caller sees which field failed
 * and why, instead of the generic message (DP-714).
 *
 * Returns null when `detail` is not an object of string messages — a string
 * `detail` is handled by the direct-field checks in extractErrorMessage.
 *
 * @param detail - The `detail` field from an API error body
 * @returns "field: message" (comma-joined for multiple fields) or null
 */
const formatDetailObject = (detail: unknown): string | null => {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) {
    return null;
  }

  const fieldErrors = Object.entries(detail as Record<string, unknown>)
    .filter(([, message]) => typeof message === "string" && message.length > 0)
    .map(([field, message]) => `${field}: ${message as string}`);

  return fieldErrors.length > 0 ? fieldErrors.join(", ") : null;
};

/**
 * Recursively extracts error message from nested error objects.
 *
 * This helper function traverses nested error structures to find meaningful error messages.
 * It handles common nesting patterns like axios errors with response.data or fetch errors
 * with nested error objects.
 *
 * @param obj - Object to extract message from
 * @param depth - Current recursion depth (prevents infinite loops)
 * @returns Extracted error message or null if not found
 *
 * @example
 * ```typescript
 * // Axios-style nested error
 * extractErrorMessage({ response: { data: { message: "Error" } } })
 * // Returns: "Error"
 *
 * // Nested error object
 * extractErrorMessage({ error: { error: { message: "Deep error" } } })
 * // Returns: "Deep error"
 * ```
 */
const extractErrorMessage = (
  obj: Record<string, unknown>,
  depth = 0
): string | null => {
  // Prevent infinite recursion (max 5 levels deep)
  if (depth > 5) return null;

  // A validation `detail` OBJECT (field -> message) is more specific than the
  // generic `message`, so prefer it. See formatDetailObject / DP-714.
  const detailObjectMessage = formatDetailObject(obj.detail);
  if (detailObjectMessage) return detailObjectMessage;

  // Try to extract message from direct fields
  const directMessage =
    (typeof obj.message === "string" ? obj.message : null) ||
    (typeof obj.error === "string" ? obj.error : null) ||
    (typeof obj.detail === "string" ? obj.detail : null) ||
    (typeof obj.description === "string" ? obj.description : null) ||
    (typeof obj.title === "string" ? obj.title : null);

  if (directMessage) return directMessage;

  // Check errors array
  if (
    obj.errors &&
    Array.isArray(obj.errors) &&
    typeof obj.errors[0]?.message === "string"
  ) {
    return obj.errors[0].message;
  }

  // Common nested structures (axios, fetch, etc.)
  const nestedPaths = ["response", "data", "error", "body"];

  for (const path of nestedPaths) {
    const nested = obj[path];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      const nestedMessage = extractErrorMessage(
        nested as Record<string, unknown>,
        depth + 1
      );
      if (nestedMessage) return nestedMessage;
    }
  }

  return null;
};

/**
 * Creates a standardized error payload from various error formats.
 *
 * This utility handles multiple error formats that can be returned from API requests:
 * - String errors (e.g., "Not Found" from text responses)
 * - Object errors with status codes (e.g., { status: 404, message: "..." })
 * - Object errors with message fields (including deeply nested structures)
 * - Axios-style errors with response.data.message
 * - Fetch-style errors with nested error objects
 * - Unknown/unexpected error formats
 *
 * The function automatically detects non-retryable errors (4xx status codes) to prevent
 * infinite retry loops in the UI. Non-retryable errors are typically client errors where
 * retrying won't help (e.g., missing permissions, resource not found).
 *
 * @param error - The error from the API request (can be string, object, or unknown)
 * @returns Standardized error payload with message and retry information
 *
 * @example
 * ```typescript
 * // String error
 * const payload = createApiErrorPayload("Not Found");
 * // { error: "Not Found", isNonRetryable: true }
 *
 * // Object error with status code
 * const payload = createApiErrorPayload({ status: 404, message: "Resource not found" });
 * // { error: "Resource not found", isNonRetryable: true }
 *
 * // Deeply nested error (axios-style)
 * const payload = createApiErrorPayload({
 *   response: { data: { error: { message: "Deep error" } } }
 * });
 * // { error: "Deep error", isNonRetryable: false }
 *
 * // Unknown error
 * const payload = createApiErrorPayload(new Error("Network failure"));
 * // { error: "Network failure", isNonRetryable: false }
 * ```
 */
export const createApiErrorPayload = (error: unknown): ApiErrorPayload => {
  let errorMessage: string;
  let isNonRetryable = false;

  // Handle different error formats that might come from the API
  if (typeof error === "string") {
    // Text response body - this is what we expect for 404s and simple errors
    errorMessage = error;

    // For text responses, check if it looks like a non-retryable error
    const lowerError = errorMessage.toLowerCase();
    isNonRetryable = NON_RETRYABLE_KEYWORDS.some(keyword =>
      lowerError.includes(keyword)
    );
  } else if (error && typeof error === "object") {
    // JSON response body or Error object
    const errorObj = error as Record<string, unknown>;

    // Use the recursive helper to extract error message
    errorMessage = extractErrorMessage(errorObj) || JSON.stringify(error);

    // Check for status codes in the error object (including nested)
    const statusCode =
      (typeof errorObj.status === "number" ? errorObj.status : null) ||
      (typeof errorObj.statusCode === "number" ? errorObj.statusCode : null) ||
      (typeof errorObj.code === "number" ? errorObj.code : null) ||
      // Check nested response objects (axios pattern)
      (errorObj.response &&
      typeof errorObj.response === "object" &&
      typeof (errorObj.response as Record<string, unknown>).status === "number"
        ? ((errorObj.response as Record<string, unknown>).status as number)
        : null);

    if (
      statusCode &&
      NON_RETRYABLE_STATUS_CODES.includes(
        statusCode as (typeof NON_RETRYABLE_STATUS_CODES)[number]
      )
    ) {
      isNonRetryable = true;
    }

    // Also check the message content for non-retryable indicators
    if (!isNonRetryable) {
      const lowerErrorMessage = errorMessage.toLowerCase();
      isNonRetryable = NON_RETRYABLE_KEYWORDS.some(keyword =>
        lowerErrorMessage.includes(keyword)
      );
    }
  } else {
    // Unknown error format
    errorMessage = "An unknown error occurred";
  }

  return {
    error: errorMessage,
    isNonRetryable
  };
};
